import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Folder, FolderPlus, Upload, File, Image, FileText, Video, Music,
  Trash2, Download, Search, Grid, List, MoreVertical, ChevronRight,
  Plus, X, Loader2, ExternalLink, Copy, Check, Cloud, CloudOff, Link2, RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Badge } from '../../components/ui/badge';
import { apiRequest } from '../../lib/utils';
import { toast } from 'sonner';

// Default folder structure
const DEFAULT_FOLDERS = [
  { id: 'logos', name: 'LOGOS', icon: 'image', color: 'bg-orange-500' },
  { id: 'photos', name: 'Photos', icon: 'image', color: 'bg-blue-500' },
  { id: 'documents', name: 'Documents', icon: 'file', color: 'bg-green-500' },
  { id: 'digital-media', name: 'Digital Media', icon: 'video', color: 'bg-purple-500' },
  { id: 'commercials', name: 'Commercials', icon: 'video', color: 'bg-red-500' },
  { id: 'marketing', name: 'Marketing Materials', icon: 'file', color: 'bg-pink-500' },
];

// Pre-loaded logo files
const PRELOADED_LOGOS = [
  { 
    id: 'logo-1', 
    name: 'Logo - Orange Background.jpg', 
    url: 'https://customer-assets.emergentagent.com/job_8967738c-eeac-4dbb-b6d7-66459e7c529e/artifacts/nt444gkn_6.jpg',
    type: 'image',
    size: '~50 KB',
    folder: 'logos'
  },
  { 
    id: 'logo-2', 
    name: 'Logo - Dark Background.jpg', 
    url: 'https://customer-assets.emergentagent.com/job_8967738c-eeac-4dbb-b6d7-66459e7c529e/artifacts/vakwseq3_4.jpg',
    type: 'image',
    size: '~50 KB',
    folder: 'logos'
  },
  { 
    id: 'logo-3', 
    name: 'Logo - Light Background.jpg', 
    url: 'https://customer-assets.emergentagent.com/job_8967738c-eeac-4dbb-b6d7-66459e7c529e/artifacts/uwa5uywm_5.jpg',
    type: 'image',
    size: '~50 KB',
    folder: 'logos'
  },
  { 
    id: 'logo-4', 
    name: 'Logo - Alternate.jpg', 
    url: 'https://customer-assets.emergentagent.com/job_8967738c-eeac-4dbb-b6d7-66459e7c529e/artifacts/jrfzrsw1_6.jpg',
    type: 'image',
    size: '~50 KB',
    folder: 'logos'
  },
];

const getFileIcon = (type) => {
  switch (type) {
    case 'image': return Image;
    case 'video': return Video;
    case 'audio': return Music;
    default: return FileText;
  }
};

const getFileType = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
  if (['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'ogg', 'aac'].includes(ext)) return 'audio';
  return 'document';
};

export default function AdminAssets() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [folders, setFolders] = useState(DEFAULT_FOLDERS);
  const [files, setFiles] = useState(PRELOADED_LOGOS);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [driveStatus, setDriveStatus] = useState({ connected: false, email: '' });
  const [connectingDrive, setConnectingDrive] = useState(false);
  const [folderMappings, setFolderMappings] = useState({});
  const [syncingFolder, setSyncingFolder] = useState(null);
  const fileInputRef = useRef(null);

  const fetchAssets = useCallback(async () => {
    try {
      const data = await apiRequest('/admin/assets');
      if (data.folders) setFolders([...DEFAULT_FOLDERS, ...data.folders.filter(f => !DEFAULT_FOLDERS.find(df => df.id === f.id))]);
      if (data.files) setFiles([...PRELOADED_LOGOS, ...data.files.filter(f => !PRELOADED_LOGOS.find(pf => pf.id === f.id))]);
    } catch (error) {
      // Use defaults if API not ready
      console.log('Using default assets');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDriveStatus = useCallback(async () => {
    try {
      const status = await apiRequest('/oauth/drive/status');
      setDriveStatus(status);
    } catch (error) {
      console.log('Could not fetch Drive status');
    }
  }, []);

  const fetchFolderMappings = useCallback(async () => {
    try {
      const data = await apiRequest('/oauth/drive/folder-mappings');
      const mappingsObj = {};
      (data.mappings || []).forEach(m => {
        mappingsObj[m.local_folder_id] = m;
      });
      setFolderMappings(mappingsObj);
    } catch (error) {
      console.log('Could not fetch folder mappings');
    }
  }, []);

  useEffect(() => {
    fetchAssets();
    fetchDriveStatus();
    fetchFolderMappings();
    
    // Check for URL params from OAuth callback
    if (searchParams.get('drive_connected') === 'true') {
      toast.success('Google Drive connected successfully!');
      fetchDriveStatus();
    } else if (searchParams.get('error') === 'drive_auth_failed') {
      toast.error('Failed to connect Google Drive. Please try again.');
    }
  }, [searchParams, fetchAssets, fetchDriveStatus, fetchFolderMappings]);

  const connectGoogleDrive = async () => {
    setConnectingDrive(true);
    try {
      const data = await apiRequest('/oauth/drive/login');
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      }
    } catch (error) {
      toast.error('Failed to initiate Google Drive connection');
      setConnectingDrive(false);
    }
  };

  const disconnectGoogleDrive = async () => {
    try {
      await apiRequest('/oauth/drive/disconnect', { method: 'POST' });
      setDriveStatus({ connected: false, email: '' });
      setFolderMappings({});
      toast.success('Google Drive disconnected');
    } catch (error) {
      toast.error('Failed to disconnect Google Drive');
    }
  };

  const syncFolderToDrive = async (folderId, folderName) => {
    if (!driveStatus.connected) {
      toast.error('Please connect Google Drive first');
      return;
    }
    
    setSyncingFolder(folderId);
    try {
      const result = await apiRequest('/oauth/drive/sync-folder', {
        method: 'POST',
        body: JSON.stringify({
          local_folder_id: folderId,
          folder_name: folderName
        })
      });
      
      setFolderMappings(prev => ({
        ...prev,
        [folderId]: {
          local_folder_id: folderId,
          local_folder_name: folderName,
          drive_folder_id: result.drive_folder.id,
          drive_link: result.drive_folder.webViewLink
        }
      }));
      
      toast.success(`Folder "${folderName}" synced to Google Drive`);
    } catch (error) {
      toast.error('Failed to sync folder');
    } finally {
      setSyncingFolder(null);
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    
    const folder = {
      id: newFolderName.toLowerCase().replace(/\s+/g, '-'),
      name: newFolderName,
      icon: 'folder',
      color: 'bg-slate-500'
    };
    
    try {
      await apiRequest('/admin/assets/folders', {
        method: 'POST',
        body: JSON.stringify(folder)
      });
      setFolders(prev => [...prev, folder]);
      toast.success('Folder created');
    } catch (error) {
      // Add locally if API fails
      setFolders(prev => [...prev, folder]);
      toast.success('Folder created');
    }
    
    setNewFolderName('');
    setShowNewFolder(false);
  };

  const deleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder and all its contents?')) return;
    
    try {
      await apiRequest(`/admin/assets/folders/${folderId}`, { method: 'DELETE' });
    } catch (error) {
      console.log('API not ready');
    }
    
    setFolders(prev => prev.filter(f => f.id !== folderId));
    setFiles(prev => prev.filter(f => f.folder !== folderId));
    if (currentFolder === folderId) setCurrentFolder(null);
    toast.success('Folder deleted');
  };

  const handleFileUpload = async (e) => {
    const uploadedFiles = e.target.files;
    if (!uploadedFiles?.length) return;
    
    setUploading(true);
    
    for (const file of uploadedFiles) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', currentFolder || 'general');
      
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        });
        
        const result = await response.json();
        
        if (response.ok) {
          const newFile = {
            id: result.id || Date.now().toString(),
            name: file.name,
            url: result.url,
            type: getFileType(file.name),
            size: `${(file.size / 1024).toFixed(1)} KB`,
            folder: currentFolder || 'general',
            uploadedAt: new Date().toISOString()
          };
          
          setFiles(prev => [...prev, newFile]);
          toast.success(`Uploaded: ${file.name}`);
        } else {
          toast.error(`Failed to upload: ${file.name}`);
        }
      } catch (error) {
        toast.error(`Failed to upload: ${file.name}`);
      }
    }
    
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const deleteFile = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    
    try {
      await apiRequest(`/admin/assets/files/${fileId}`, { method: 'DELETE' });
    } catch (error) {
      console.log('API not ready');
    }
    
    setFiles(prev => prev.filter(f => f.id !== fileId));
    toast.success('File deleted');
  };

  const copyToClipboard = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      toast.success('URL copied to clipboard');
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const filteredFiles = files.filter(file => {
    const matchesFolder = currentFolder ? file.folder === currentFolder : true;
    const matchesSearch = file.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFolder && matchesSearch;
  });

  const getFolderFileCount = (folderId) => {
    return files.filter(f => f.folder === folderId).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div data-testid="admin-assets" className="pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Corporate Assets
          </h1>
          <p className="text-slate-500 mt-1">Manage logos, photos, documents, and media files</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowNewFolder(true)}>
            <FolderPlus className="w-4 h-4 mr-2" />
            New Folder
          </Button>
          <Button 
            onClick={() => fileInputRef.current?.click()} 
            className="bg-orange-600 hover:bg-orange-700"
            disabled={uploading}
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Upload Files
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            multiple
            className="hidden"
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
          />
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-6 text-sm">
        <button 
          onClick={() => setCurrentFolder(null)}
          className={`hover:text-orange-600 ${!currentFolder ? 'text-orange-600 font-medium' : 'text-slate-600'}`}
        >
          All Assets
        </button>
        {currentFolder && (
          <>
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <span className="text-orange-600 font-medium">
              {folders.find(f => f.id === currentFolder)?.name}
            </span>
          </>
        )}
      </div>

      {/* Search and View Toggle */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-1 border rounded-lg p-1">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('grid')}
            className={viewMode === 'grid' ? 'bg-orange-600 hover:bg-orange-700' : ''}
          >
            <Grid className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
            className={viewMode === 'list' ? 'bg-orange-600 hover:bg-orange-700' : ''}
          >
            <List className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Google Drive Connection Card */}
      <Card className="border-none shadow-sm mb-6">
        <CardContent className="p-4">
          {driveStatus.connected ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Cloud className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-green-800">Google Drive Connected</p>
                  <p className="text-sm text-green-600">{driveStatus.email}</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={disconnectGoogleDrive} className="text-red-600 hover:text-red-700">
                <CloudOff className="w-4 h-4 mr-1" />
                Disconnect
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Cloud className="w-5 h-5 text-slate-400" />
                </div>
                <div>
                  <p className="font-medium text-slate-700">Connect Google Drive</p>
                  <p className="text-sm text-slate-500">Sync folders to your Google Drive account</p>
                </div>
              </div>
              <Button onClick={connectGoogleDrive} disabled={connectingDrive} className="bg-blue-600 hover:bg-blue-700">
                {connectingDrive ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Cloud className="w-4 h-4 mr-2" />}
                Connect Drive
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Folders */}
      {!currentFolder && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Folders</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {folders.map((folder) => {
              const mapping = folderMappings[folder.id];
              const isSynced = !!mapping;
              
              return (
                <Card 
                  key={folder.id} 
                  className="border-none shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
                  onClick={() => setCurrentFolder(folder.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-12 h-12 rounded-xl ${folder.color} flex items-center justify-center relative`}>
                        <Folder className="w-6 h-6 text-white" />
                        {isSynced && (
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                            <Cloud className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          {driveStatus.connected && (
                            <>
                              {isSynced ? (
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); window.open(mapping.drive_link, '_blank'); }}>
                                  <ExternalLink className="w-4 h-4 mr-2" />
                                  Open in Drive
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); syncFolderToDrive(folder.id, folder.name); }}>
                                  {syncingFolder === folder.id ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <Link2 className="w-4 h-4 mr-2" />
                                  )}
                                  Sync to Drive
                                </DropdownMenuItem>
                              )}
                            </>
                          )}
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); deleteFolder(folder.id); }}>
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <p className="font-medium text-slate-900 truncate">{folder.name}</p>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-slate-500">{getFolderFileCount(folder.id)} files</p>
                      {isSynced && (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          <Cloud className="w-3 h-3 mr-1" />
                          Synced
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Files */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">
          {currentFolder ? `Files in ${folders.find(f => f.id === currentFolder)?.name}` : 'Recent Files'}
        </h2>
        
        {filteredFiles.length === 0 ? (
          <Card className="border-none shadow-sm">
            <CardContent className="p-12 text-center">
              <File className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No files found</p>
              <p className="text-sm text-slate-400 mt-1">Upload files or select a folder</p>
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {filteredFiles.map((file) => {
              const FileIcon = getFileIcon(file.type);
              return (
                <Card 
                  key={file.id} 
                  className="border-none shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
                  onClick={() => setSelectedFile(file)}
                >
                  <CardContent className="p-3">
                    <div className="aspect-square rounded-lg bg-slate-100 mb-2 overflow-hidden flex items-center justify-center">
                      {file.type === 'image' ? (
                        <img src={file.url} alt={file.name} className="w-full h-full object-cover" />
                      ) : (
                        <FileIcon className="w-12 h-12 text-slate-400" />
                      )}
                    </div>
                    <p className="text-sm font-medium text-slate-900 truncate">{file.name}</p>
                    <p className="text-xs text-slate-500">{file.size}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="border-none shadow-sm">
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Name</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Type</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Size</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFiles.map((file) => {
                    const FileIcon = getFileIcon(file.type);
                    return (
                      <tr key={file.id} className="border-t hover:bg-slate-50">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden">
                              {file.type === 'image' ? (
                                <img src={file.url} alt={file.name} className="w-full h-full object-cover" />
                              ) : (
                                <FileIcon className="w-5 h-5 text-slate-400" />
                              )}
                            </div>
                            <span className="font-medium text-slate-900">{file.name}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge variant="outline">{file.type}</Badge>
                        </td>
                        <td className="p-4 text-slate-600">{file.size}</td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm" onClick={() => setSelectedFile(file)}>
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => copyToClipboard(file.url)}>
                              {copiedUrl === file.url ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteFile(file.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* New Folder Dialog */}
      <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
            <DialogDescription>Enter a name for the new folder</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && createFolder()}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowNewFolder(false)}>Cancel</Button>
            <Button onClick={createFolder} className="bg-orange-600 hover:bg-orange-700">Create</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* File Preview Dialog */}
      <Dialog open={!!selectedFile} onOpenChange={() => setSelectedFile(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedFile?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {selectedFile?.type === 'image' && (
              <img src={selectedFile.url} alt={selectedFile.name} className="w-full rounded-lg" />
            )}
            {selectedFile?.type === 'video' && (
              <video src={selectedFile.url} controls className="w-full rounded-lg" />
            )}
            {selectedFile?.type !== 'image' && selectedFile?.type !== 'video' && (
              <div className="text-center py-12 bg-slate-100 rounded-lg">
                <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600">{selectedFile?.name}</p>
              </div>
            )}
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600 mb-2">File URL:</p>
              <div className="flex gap-2">
                <Input value={selectedFile?.url} readOnly className="text-xs" />
                <Button variant="outline" onClick={() => copyToClipboard(selectedFile?.url)}>
                  {copiedUrl === selectedFile?.url ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => deleteFile(selectedFile?.id)}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
            <div className="flex gap-2">
              <a href={selectedFile?.url} target="_blank" rel="noopener noreferrer">
                <Button variant="outline">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open
                </Button>
              </a>
              <a href={selectedFile?.url} download={selectedFile?.name}>
                <Button className="bg-orange-600 hover:bg-orange-700">
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
